import { MeasureNumber, MeasureVisit } from '../../../common/src/syp_file.ts';

// The verse bar's index 0 is the introduction ("Int") when one is on, and verse 1 otherwise; verse numbers are the
// 1-based verses a singer counts (MeasureVisit.verse).

export function verseNumberFromIndex(verseIndex: number, hasIntroduction: boolean): number {
  return hasIntroduction ? verseIndex : verseIndex + 1;
}

export function verseIndexFromNumber(verseNumber: number, hasIntroduction: boolean): number {
  return hasIntroduction ? verseNumber : verseNumber - 1;
}

// the number of verses sung in a song with repeats (0 when there are no visits)
export function getVerseCount(visits: MeasureVisit[]): number {
  return visits.reduce((count, { verse }) => Math.max(count, verse), 0);
}

// the index of the verse's first visit, undefined when no visit belongs to the verse
export function getVerseStartVisitIndex(visits: MeasureVisit[], verseNumber: number): number | undefined {
  const visitIndex = visits.findIndex(({ verse }) => verse === verseNumber);
  return visitIndex === -1 ? undefined : visitIndex;
}

// where the playback position is in a song with repeats: the verse, and the pass when it is known (not on the
// introduction)
export type VisitPass = Pick<MeasureVisit, 'verse'> & Partial<Pick<MeasureVisit, 'iteration'>>;

// The visit a seek to the measure lands on, keeping the current pass: a nudge inside a section a verse sings twice
// (499-3's verse 3 plays B, B, bridge, bridge, B) stays in the same visit of it. Among the measure's visits, the one
// in the current pass of the current verse (a nested repeat runs passes 1 and 2 in every verse), else the verse's
// first, else the last verse's first; undefined when the song never visits it. In a song without begin_verse, verse
// and pass are the same number.
export function findVisitIndexToSeek(
  visits: MeasureVisit[],
  measureNumber: MeasureNumber,
  { verse, iteration }: VisitPass,
): number | undefined {
  const candidates = visits.flatMap((visit, index) =>
    visit.measure.number === measureNumber ? [{ visit, index }] : [],
  );
  if (candidates.length === 0) {
    return undefined;
  }
  const lastVerse = Math.max(...candidates.map(({ visit }) => visit.verse));
  const best =
    candidates.find(({ visit }) => visit.verse === verse && visit.iteration === iteration) ??
    candidates.find(({ visit }) => visit.verse === verse) ??
    candidates.find(({ visit }) => visit.verse === lastVerse);
  return best?.index;
}
